import { useHandleError } from "@/hooks/useHandleError";
import { usePostPointWithdrawalRequest } from "@roman/shared/hooks/withdrawals/usePostPointWithdrawal";
import type { PointWithdrawalType } from "@roman/shared/types/PointWithdrawal";
import { yenToPoints } from "@roman/shared/utils/pointsToYen";
import { useMemo, useState } from "react";
import { PaymentForm } from "./Payment";
import { WithdrawalConfirmationPage } from "./Payment/Confirmation";
import { WithDrawalPage } from "./PaymentMethod";
import { WithdrawalSuccessPage } from "./Success";
import { METHOD_CONTENT } from "./constant";
import { calculateTotalPointsForRequest, calculateTotalPointsWithCommission } from "./utils";

export enum PaymentHeading {
  Amazon = "Amazonギフトカード",
  Apple = "Apple Gift Card",
  Google = "Google Playギフトカード",
  Selective = "えらべるPay",
  Giftee = "giftee Box Select",
}

enum PageStep {
  payment = "PaymentMethod",
  form = "Form",
  confirmation = "Confirmation",
  success = "Success",
}

export type CardQuantity = {
  yenOption: string;
  quantity: number;
};

export type MethodContent = {
  method: PointWithdrawalType;
  exchangeRange: string;
  availableYenOptions: string[];
  description: string;
  iconSrc: string;
  heading: string;
};

export const WithdrawalHome = () => {
  const [cardQuantities, setCardQuantities] = useState<CardQuantity[]>([]);
  const [methodContent, setMethodContent] = useState<MethodContent | null>(null);
  const [step, setStep] = useState<PageStep>(PageStep.payment);

  const totalConsumingPoints = useMemo(() => {
    return calculateTotalPointsWithCommission(cardQuantities);
  }, [cardQuantities]);

  const handleError = useHandleError("出金に失敗しました");
  const { data: pointWithdrawalId, trigger } = usePostPointWithdrawalRequest(handleError);

  const handlePaymentMethod = ({
    selectedMethod,
  }: {
    selectedMethod: PointWithdrawalType;
  }) => {
    const content = METHOD_CONTENT.find((methodContent: MethodContent) => methodContent.method === selectedMethod);
    if (content) {
      setMethodContent(content);
    }
    setStep(PageStep.form);
  };

  const handleFormConfirming = () => {
    setStep(PageStep.confirmation);
  };

  const handleConfirmation = async () => {
    if (!methodContent) {
      throw new Error("Method is not selected");
    }

    if (methodContent.method === "Apple" && calculateTotalPointsWithCommission(cardQuantities) < yenToPoints(1000)) {
      throw new Error("Apple Payでは1000円以下の出金はできません");
    }

    // BEへ送るときは手数料を引いたpointsを送る
    await trigger({
      type: methodContent.method,
      redeemablePoints: calculateTotalPointsForRequest(totalConsumingPoints).toString(),
    });
    setStep(PageStep.success);
  };

  return (
    <div>
      {step === "PaymentMethod" && <WithDrawalPage onClick={handlePaymentMethod} />}
      {step === "Form" && (
        <PaymentForm
          methodContent={methodContent}
          totalConsumingPoints={totalConsumingPoints}
          cardQuantities={cardQuantities}
          setCardQuantities={setCardQuantities}
          backButtonClick={() => {
            setStep(PageStep.payment);
            setCardQuantities([]);
          }}
          onClick={handleFormConfirming}
        />
      )}
      {step === "Confirmation" && methodContent && (
        <WithdrawalConfirmationPage
          methodContent={methodContent}
          totalConsumingPoints={totalConsumingPoints}
          cardQuantities={cardQuantities}
          backButtonClick={() => setStep(PageStep.form)}
          onClick={handleConfirmation}
        />
      )}
      {step === "Success" && pointWithdrawalId && (
        <WithdrawalSuccessPage id={pointWithdrawalId} totalConsumingPoints={totalConsumingPoints} />
      )}
    </div>
  );
};
